import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/Common";
import { CardState, SubHeader, WbCard } from "@/components/wb/WbUi";
import { api, enc } from "@/lib/api";
import { useWb, wbError } from "@/lib/wb";
import { cn } from "@/lib/utils";

const KINDS = ["wisdom", "thinker", "question", "challenge", "boost", "journey"];
const JOURNEY_FIELDS = ["title", "subtitle", "intro", "order"];
const pill = (it) => (it.active === false ? ["INACTIVE", "border-zinc-600 text-zinc-500"] : it.status === "approved" ? ["LIVE", "border-emerald-500/50 text-emerald-400"] : ["DRAFT", "border-amber-500/50 text-amber-400"]);
const toForm = (fields, it = {}) => Object.fromEntries(Object.keys(fields).map((k) => [k, fields[k] === "list" ? (it[k] || []).join("\n") : it[k] ?? ""]));
const fromForm = (fields, f) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k, fields[k] === "list" ? String(v).split("\n").map((s) => s.trim()).filter(Boolean) : fields[k] === "int" ? Number(v) || 0 : v]));

const Editor = ({ kind, fields, item, onDone }) => {
  const f0 = kind === "journey" ? Object.fromEntries(Object.entries(fields).filter(([k]) => JOURNEY_FIELDS.includes(k))) : fields;
  const [f, setF] = useState(() => toForm(f0, item));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { item ? await api.patch(`/admin/wellbeing/content/${enc(item.content_id)}`, { fields: fromForm(f0, f) }) : await api.post("/admin/wellbeing/content", { kind, fields: fromForm(f0, f) }); toast.success(item ? "Saved" : "Draft created"); onDone(); }
    catch (e) { wbError(e); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-[#27272A] bg-[#09090B] p-4" data-testid="wb-admin-editor">
      {Object.entries(f0).map(([k, t]) => (
        <label key={k} className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">{k}{t === "list" && " (one per line)"}{k === "quote_kind" && " · \"quote\" = verified wording with source, \"paraphrase\" = our own summary"}</span>
          {t === "list" || ["text", "intro", "bio", "idea", "prompt"].includes(k) ? <Textarea rows={3} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-[#121214]" data-testid={`wb-admin-field-${k}`} /> : <Input type={t === "int" ? "number" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-[#121214]" data-testid={`wb-admin-field-${k}`} />}
        </label>
      ))}
      <div className="flex gap-2"><Button size="sm" onClick={save} disabled={busy} data-testid="wb-admin-save">{busy ? <Spinner /> : "Save"}</Button><Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button></div>
    </div>
  );
};

export default function AdminWellbeing() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/wellbeing" replace />;
  return <AdminWellbeingInner />;
}

function AdminWellbeingInner() {
  const [kind, setKind] = useState("wisdom");
  const q = useWb(`/admin/wellbeing/content?kind=${kind}`);
  const [edit, setEdit] = useState(null);
  const fields = q.data?.fields || {};
  const patch = async (it, body, msg) => { try { await api.patch(`/admin/wellbeing/content/${enc(it.content_id)}`, body); toast.success(msg); q.reload(); } catch (e) { wbError(e); } };
  const del = async (it) => { if (!window.confirm("Delete this item? Built-in items are deactivated instead.")) return; try { await api.delete(`/admin/wellbeing/content/${enc(it.content_id)}`); toast.success("Removed"); q.reload(); } catch (e) { wbError(e); } };
  const done = () => { setEdit(null); q.reload(); };
  return (
    <div className="space-y-6" data-testid="wb-admin">
      <SubHeader title="Well-Being Content" subtitle="Draft, approve and manage the content shared by web and mobile." back="/admin" />
      <div className="flex flex-wrap gap-2">{KINDS.map((k) => <button key={k} onClick={() => { setKind(k); setEdit(null); }} className={cn("rounded-full border px-4 py-1.5 text-sm capitalize", kind === k ? "border-[#D4AF37] bg-[#33290D] text-[#F3C649]" : "border-[#27272A] text-zinc-400")} data-testid={`wb-admin-kind-${k}`}>{k}</button>)}</div>
      <WbCard>
        {kind !== "journey" && <Button size="sm" variant="outline" onClick={() => setEdit("new")} data-testid="wb-admin-new"><Plus className="h-4 w-4" />New {kind}</Button>}
        {edit === "new" && <Editor kind={kind} fields={fields} onDone={done} />}
        <CardState q={q} what="Admin content"><div className="mt-4 space-y-3">{(q.data?.items || []).map((it) => {
          const [label, cls] = pill(it);
          return (
            <div key={it.content_id} className="rounded-2xl border border-[#27272A] p-4" data-testid={`wb-admin-item-${it.content_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wider", cls)} data-testid={`wb-admin-status-${it.content_id}`}>{label}</span>
                <span className="min-w-0 flex-1 truncate font-wb text-zinc-100">{it.title || it.name || it.text}</span>
                <label className="flex items-center gap-1.5 text-xs text-zinc-400">Active<Switch checked={it.active !== false} onCheckedChange={(v) => patch(it, { active: v }, v ? "Activated" : "Deactivated")} data-testid={`wb-admin-active-${it.content_id}`} /></label>
                {it.status !== "approved" && <Button size="sm" onClick={() => patch(it, { status: "approved" }, "Approved · now live")} data-testid={`wb-admin-approve-${it.content_id}`}>Approve</Button>}
                <Button size="sm" variant="ghost" onClick={() => setEdit(edit === it.content_id ? null : it.content_id)} data-testid={`wb-admin-edit-${it.content_id}`}>Edit</Button>
                {kind !== "journey" && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(it)} data-testid={`wb-admin-delete-${it.content_id}`}>Delete</Button>}
              </div>
              {edit === it.content_id && <Editor kind={kind} fields={fields} item={it} onDone={done} />}
            </div>);
        })}</div></CardState>
      </WbCard>
    </div>
  );
}
